import { supabase } from './supabaseClient';

/**
 * SMS Service for sending messages via Supabase Edge Function
 */
export class SmsService {
  static INTERNAL_API_KEY = process.env.INTERNAL_API_KEY || 'super-secret';

  /**
   * Send SMS to one or more phone numbers
   * @param {string} message - The message to send
   * @param {string|string[]} numbers - Phone number(s) to send to
   * @param {string} transactionId - Optional transaction ID
   * @returns {Promise<Object>} - Send result
   */
  static async sendSms(message, numbers, transactionId) {
    try {
      if (!message?.trim()) {
        throw new Error('Message is required.');
      }

      const numberArray = Array.isArray(numbers) ? numbers : [numbers];
      if (!numberArray.length) {
        throw new Error('At least one phone number is required.');
      }

      console.log(`Sending SMS to ${numberArray.length} number(s)...`);

      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: {
          message: message.trim(),
          numbers: numberArray,
          transaction_id: transactionId || String(Date.now())
        },
        headers: {
          'x-internal-key': this.INTERNAL_API_KEY
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to send SMS via edge function');
      }

      console.log('SMS sent successfully:', data);
      return {
        success: true,
        data,
        message: 'SMS sent successfully'
      };

    } catch (error) {
      console.error('Error sending SMS:', error);
      return {
        success: false,
        error: error.message || 'Failed to send SMS',
        message: 'Error sending SMS'
      };
    }
  }

  /**
   * Send welcome SMS to a lead after submission
   * @param {Object} submission - The lead submission object
   * @returns {Promise<Object>} - Send result
   */
  static async sendWelcomeSms(submission) {
    try {
      if (!submission?.phone) {
        throw new Error('Phone number is required for welcome SMS');
      }

      const firstName = submission.first_name || 'Valued Client';
      const selectedCountry = submission.country_selection?.selectedCountry || 'Germany';
      const selectedPathway = submission.selected_pathway || 'your preferred pathway';

      const message = `Dear ${firstName},

Thank you for submitting your immigration profile to Ravana Migration!

We have received your application for ${selectedCountry} immigration via ${selectedPathway}.

Our immigration experts will review your profile and contact you within 24-48 hours with personalized recommendations.

Best regards,
Ravana Migration Team
🇱🇰 | info@ravanaindustries.com | www.ravana-migration.com`;

      const result = await this.sendSms(message, submission.phone);
      return result;

    } catch (error) {
      console.error('Error sending welcome SMS:', error);
      return {
        success: false,
        error: error.message || 'Failed to send welcome SMS',
        message: 'Error sending welcome SMS'
      };
    }
  }

  /**
   * Normalize Sri Lankan phone number
   * @param {string} phone - Raw phone number
   * @returns {string|null} - Normalized phone number
   */
  static normalizePhone(phone) {
    if (!phone) return null;
    let s = phone.replace(/\D/g, '');

    if (s.startsWith('0094')) s = '0' + s.slice(4);
    else if (s.startsWith('94')) s = '0' + s.slice(2);
    else if (s.length === 9 && s.startsWith('7')) s = '0' + s;

    return s;
  }

  /**
   * Validate Sri Lankan phone number
   * @param {string} phone - Phone number to validate
   * @returns {boolean} - True if valid
   */
  static isValidSriLankanPhone(phone) {
    const normalized = this.normalizePhone(phone);
    if (!normalized || normalized.length !== 10) return false;

    const prefixes = ['070', '071', '072', '074', '075', '076', '077', '078'];
    return prefixes.some(p => normalized.startsWith(p));
  }
}

export default SmsService;
